import { useEffect, useState } from 'react'
import { useEvaluatorPack } from '../addons'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'

/**
 * The Excel add-in — TECHNICAL_DESIGN.md §4, proposal §D, IMPLEMENTATION.md decision 40.
 *
 * What the add-in is, the file that adds it to Excel, and how to use it once it is
 * there. The file is this appliance's own: it names the address it was downloaded
 * from, so the screen says which, read from the file itself, and a person can see
 * the add-in will open this installation and nothing else.
 */

const MANIFEST = '/addin/manifest.xml'

function downloadManifest() {
  const link = document.createElement('a')
  link.href = MANIFEST
  link.download = 'anubandh-addin-manifest.xml'
  link.click()
}

export function ExcelAddin() {
  const pack = useEvaluatorPack()
  const [opens, setOpens] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetch(MANIFEST, { credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`The add-in file could not be prepared (${response.status}).`)
        const found = /<SourceLocation DefaultValue="([^"]+)"/.exec(await response.text())
        if (live) setOpens(found?.[1] ?? null)
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [])

  return (
    <section className="excel-addin">
      <header className="page-head">
        <h2>Excel add-in</h2>
        <p className="note">
          Look up a fund from a cell in Excel: its limits in force
          {pack ? ', and its latest verdicts' : ''}, beside the spreadsheet you are working in. The
          add-in reads the cell you select and writes nothing into your workbook.
          {!pack && (
            <>
              {' '}
              With the <EvaluatorName strong={false} />, it shows the fund’s latest verdicts too.
              <AddOnFootnote />
            </>
          )}
        </p>
      </header>

      <ol className="addin-steps">
        <li className="card">
          <h3>Download the add-in file</h3>
          <p>It tells Excel where this installation is, and names nothing else.</p>
          <button type="button" onClick={downloadManifest} disabled={Boolean(error)}>
            Download the add-in file
          </button>
          {opens && (
            <p className="clause" data-testid="addin-opens">
              The add-in opens {opens}
            </p>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </li>
        <li className="card">
          <h3>Add it to Excel</h3>
          <p>
            <strong>For yourself</strong>, in Excel on the web: <strong>Home → Add-ins → More add-ins → My
            add-ins → Upload my add-in</strong>, then choose the file.
          </p>
          <p>
            <strong>For your firm</strong>, in Excel for Windows, Mac and the web: a Microsoft 365
            administrator uploads the same file in the Microsoft 365 admin center, under{' '}
            <strong>Settings → Integrated apps → Upload custom apps</strong>, and chooses who gets it.
          </p>
        </li>
        <li className="card">
          <h3>Use it</h3>
          <ol>
            <li>
              Open <strong>GUARD guidelines</strong> from <strong>Home → Add-ins</strong>. A panel opens
              beside the sheet.
            </li>
            <li>
              Sign in with Microsoft, which opens in a small window, or with your GUARD email and
              password.
            </li>
            <li>
              Select a cell holding a fund’s code, such as HALVEN, and choose{' '}
              <strong>Use selected cell</strong>. Select another fund’s cell and choose it again.
            </li>
            <li>
              <strong>Guidelines</strong> shows the fund’s limits in force.
              {pack && (
                <>
                  {' '}
                  <strong>Verdicts</strong> shows its latest verdicts, each with the day its holdings
                  were as at, and offers to evaluate where newer holdings or changed limits have
                  overtaken them.
                </>
              )}
            </li>
          </ol>
        </li>
      </ol>
    </section>
  )
}
